import * as ActionTypes from "../actions/actiontypes"

export const initialState = {
    // Data
    routeSummaryData: [],
    routeDetailsData: [],
    doorDetailsData: [],
    
    // Filter Options
    filterOptions: {
        dates: [],
        customers: [],
        dcs: [],
        routes: [],
        doors: []
    },
    
    // Active Filters
    filters: {
        date: null,
        customer: null,
        dc: null,
        route: null,
        door: null
    },
    
    // Loading States
    loading: {
        routeSummary: false,
        routeDetails: false,
        doorDetails: false,
        filterOptions: false
    },
    
    // Error States
    error: {
        routeSummary: null,
        routeDetails: null,
        doorDetails: null
    },
    
    // Pagination
    pagination: {
        routeSummary: {
            page: 0,
            pageSize: 50,
            totalCount: 0
        },
        routeDetails: {
            page: 0,
            pageSize: 50,
            totalCount: 0
        },
        doorDetails: {
            page: 0,
            pageSize: 50,
            totalCount: 0
        }
    },
    
    // Sorting
    sorting: {
        routeSummary: {
            sortBy: null,
            sortOrder: 'asc'
        },
        routeDetails: {
            sortBy: null,
            sortOrder: 'asc'
        },
        doorDetails: {
            sortBy: null,
            sortOrder: 'asc'
        }
    },
    
    // Performance Tracking
    lastRequestTime: {
        routeSummary: null,
        routeDetails: null,
        doorDetails: null
    }
}

const LoadValidationDashboardReducer = (state = initialState, action) => {
    switch (action.type) {
        // Filter Options
        case ActionTypes.LOAD_LVD_FILTER_OPTIONS:
            return {
                ...state,
                loading: { ...state.loading, filterOptions: true }
            };
        
        case ActionTypes.LOAD_LVD_FILTER_OPTIONS_SUCCESS:
            return {
                ...state,
                loading: { ...state.loading, filterOptions: false },
                filterOptions: action.payload.filterOptions
            };
        
        case ActionTypes.LOAD_LVD_FILTER_OPTIONS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, filterOptions: false }
            };
        
        case ActionTypes.UPDATE_LVD_FILTERS: {
            const { filterType, value } = action.payload;
            console.log(`[LVD] Filter changed: ${filterType} = ${JSON.stringify(value)} at ${new Date().toISOString()}`);
            return {
                ...state,
                filters: { ...state.filters, [filterType]: value }
            };
        }
        
        // Route Summary
        case ActionTypes.LOAD_ROUTE_SUMMARY:
            return {
                ...state,
                loading: { ...state.loading, routeSummary: true },
                error: { ...state.error, routeSummary: null }
            };
        
        case ActionTypes.LOAD_ROUTE_SUMMARY_SUCCESS:
            const routeMap = new Map();
            (action.payload.data || []).forEach(r => {
                if (r.route_system_id && r.local_route) {
                    routeMap.set(r.route_system_id, { id: r.route_system_id, label: r.local_route });
                }
            });
            const uniqueRoutes = Array.from(routeMap.values());
            return {
                ...state,
                loading: { ...state.loading, routeSummary: false },
                routeSummaryData: action.payload.data || [],
                filterOptions: {
                    ...state.filterOptions,
                    routes: uniqueRoutes
                },
                pagination: {
                    ...state.pagination,
                    routeSummary: {
                        totalCount: action.payload.totalCount || 0,
                        page: action.payload.page || 0,
                        pageSize: action.payload.pageSize || 50
                    }
                },
                lastRequestTime: { ...state.lastRequestTime, routeSummary: action.payload.responseTime },
                error: { ...state.error, routeSummary: null }
            };
        
        case ActionTypes.LOAD_ROUTE_SUMMARY_ERROR:
            return {
                ...state,
                loading: { ...state.loading, routeSummary: false },
                error: { ...state.error, routeSummary: errorDescription(action.error) }
            };
        
        case ActionTypes.UPDATE_ROUTE_SUMMARY_SORT:
            return {
                ...state,
                sorting: {
                    ...state.sorting,
                    routeSummary: {
                        sortBy: action.payload.sortBy,
                        sortOrder: action.payload.sortOrder
                    }
                }
            };
        
        case ActionTypes.UPDATE_ROUTE_SUMMARY_PAGE:
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    routeSummary: {
                        ...state.pagination.routeSummary,
                        page: action.payload.page
                    }
                }
            };
        
        // Route Details
        case ActionTypes.LOAD_ROUTE_DETAILS:
            return {
                ...state,
                loading: { ...state.loading, routeDetails: true },
                error: { ...state.error, routeDetails: null }
            };
        
        case ActionTypes.LOAD_ROUTE_DETAILS_SUCCESS:
            return {
                ...state,
                loading: { ...state.loading, routeDetails: false },
                routeDetailsData: action.payload.data || [],
                pagination: {
                    ...state.pagination,
                    routeDetails: {
                        totalCount: action.payload.totalCount || 0,
                        page: action.payload.page || 0,
                        pageSize: action.payload.pageSize || 50
                    }
                },
                lastRequestTime: { ...state.lastRequestTime, routeDetails: action.payload.responseTime },
                error: { ...state.error, routeDetails: null }
            };
        
        case ActionTypes.LOAD_ROUTE_DETAILS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, routeDetails: false },
                error: { ...state.error, routeDetails: errorDescription(action.error) }
            };
        
        case ActionTypes.UPDATE_ROUTE_DETAILS_SORT:
            return {
                ...state,
                sorting: {
                    ...state.sorting,
                    routeDetails: {
                        sortBy: action.payload.sortBy,
                        sortOrder: action.payload.sortOrder
                    }
                }
            };
        
        case ActionTypes.UPDATE_ROUTE_DETAILS_PAGE:
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    routeDetails: {
                        ...state.pagination.routeDetails,
                        page: action.payload.page
                    }
                }
            };
        
        // Door Details
        case ActionTypes.LOAD_DOOR_DETAILS:
            return {
                ...state,
                loading: { ...state.loading, doorDetails: true },
                error: { ...state.error, doorDetails: null }
            };
        
        case ActionTypes.LOAD_DOOR_DETAILS_SUCCESS:
            const uniqueDoors = [...new Set((action.payload.data || []).map(d => d.door_name).filter(Boolean))];
            return {
                ...state,
                loading: { ...state.loading, doorDetails: false },
                doorDetailsData: action.payload.data || [],
                filterOptions: {
                    ...state.filterOptions,
                    doors: uniqueDoors
                },
                pagination: {
                    ...state.pagination,
                    doorDetails: {
                        totalCount: action.payload.totalCount || 0,
                        page: action.payload.page || 0,
                        pageSize: action.payload.pageSize || 50
                    }
                },
                lastRequestTime: { ...state.lastRequestTime, doorDetails: action.payload.responseTime },
                error: { ...state.error, doorDetails: null }
            };
        
        case ActionTypes.LOAD_DOOR_DETAILS_ERROR:
            return {
                ...state,
                loading: { ...state.loading, doorDetails: false },
                error: { ...state.error, doorDetails: errorDescription(action.error) }
            };
        
        case ActionTypes.UPDATE_DOOR_DETAILS_SORT:
            return {
                ...state,
                sorting: {
                    ...state.sorting,
                    doorDetails: {
                        sortBy: action.payload.sortBy,
                        sortOrder: action.payload.sortOrder
                    }
                }
            };
        
        case ActionTypes.UPDATE_DOOR_DETAILS_PAGE:
            return {
                ...state,
                pagination: {
                    ...state.pagination,
                    doorDetails: {
                        ...state.pagination.doorDetails,
                        page: action.payload.page
                    }
                }
            };
        
        default:
            return state;
    }
}

const errorDescription = (error) => {
    if (error !== undefined && error.response != null) {
        if (error.response.data != null && error.response.data !== undefined) {
            if (error.response.data.Error) {
                return error.response.data.Error;
            } else {
                return error.response.data;
            }
        }
    } else if (error && error.code === 'ECONNABORTED') {
        return 'Request timed out. Please try again with more specific filters.';
    } else {
        return error;
    }
}

export default LoadValidationDashboardReducer;
